import { isWoodMaterialToken, normalizeSize, normalizeToken, variantColorToToken } from "./normalize";
import type { ShopifyJsProduct, ShopifyJsVariant } from "./types";

const WOOD_URL_ALIASES: Record<string, readonly string[]> = {
  eiche: ["eiche", "eich"],
  birke: ["birke"],
  buche: ["buche"],
  kiefer: ["kiefer"],
};

const WOOD_TOKENS = Object.keys(WOOD_URL_ALIASES);
const SIZE_PAIR_IN_TOKEN_RE = /(\d{2,3})x(\d{2,3})/g;

const variantOptionParts = (variant: ShopifyJsVariant): string[] =>
  [variant.option1, variant.option2, variant.option3].filter(
    (value): value is string => Boolean(value?.trim()) && value !== "Default Title",
  );

const variantWoodToken = (variant: ShopifyJsVariant): string | null => {
  for (const part of variantOptionParts(variant)) {
    if (isWoodMaterialToken(part)) return normalizeToken(part);
  }
  return null;
};

const variantSizeToken = (variant: ShopifyJsVariant): string | null => {
  for (const part of variantOptionParts(variant)) {
    const size = normalizeSize(part);
    if (/^\d{2,3}x\d{2,3}$/.test(size)) return size;
  }
  return null;
};

const woodInHaystack = (haystack: string): string | null => {
  for (const wood of WOOD_TOKENS) {
    for (const alias of WOOD_URL_ALIASES[wood] ?? []) {
      if (haystack.includes(alias)) return wood;
    }
  }
  return null;
};

/** Sizes embedded in a normalized filename token (140x200 inside …buche140x200jpg). */
const sizesInHaystack = (haystack: string): string[] => {
  const sizes = new Set<string>();
  for (const match of haystack.matchAll(SIZE_PAIR_IN_TOKEN_RE)) {
    sizes.add(normalizeSize(`${match[1]}x${match[2]}`));
  }
  return [...sizes];
};

const imageMatchNeedles = (part: string): string[] => {
  const raw = normalizeToken(part);
  if (/^\d{2,3}x\d{2,3}$/.test(normalizeSize(part))) return [];
  const needles = new Set<string>([raw]);
  // Natur-Weiß must not pick plain-weiß filenames (and vice versa).
  if (raw === "naturweiss") {
    needles.add("naturweiss");
  } else {
    const colorToken = variantColorToToken(part);
    if (colorToken !== raw) needles.add(colorToken);
  }
  for (const [wood, aliases] of Object.entries(WOOD_URL_ALIASES)) {
    if (raw === wood || raw.includes(wood)) {
      for (const alias of aliases) needles.add(alias);
    }
  }
  return [...needles].filter((needle) => needle.length >= 3);
};

const variantColorNorm = (variant: ShopifyJsVariant): string | null => {
  for (const part of variantOptionParts(variant)) {
    if (isWoodMaterialToken(part)) continue;
    if (/^\d{2,3}x\d{2,3}$/.test(normalizeSize(part))) continue;
    const n = normalizeToken(part);
    if (n.includes("weiss") || n.includes("grau") || n.includes("klarlack") || n.includes("natur")) return n;
  }
  return null;
};

const scoreImageForVariant = (imageSrc: string, variant: ShopifyJsVariant): number => {
  const haystack = normalizeToken(imageSrc.split("/").pop() ?? imageSrc);
  if (!haystack) return 0;

  const colorNorm = variantColorNorm(variant);
  if (colorNorm === "weiss" && haystack.includes("naturweiss")) return -1;
  if (colorNorm === "naturweiss" && haystack.includes("weiss") && !haystack.includes("naturweiss")) return -1;

  const wantWood = variantWoodToken(variant);
  const imageWood = woodInHaystack(haystack);
  if (wantWood && imageWood && imageWood !== wantWood) return -1;

  const wantSize = variantSizeToken(variant);
  const imageSizes = sizesInHaystack(haystack);
  if (wantSize && imageSizes.length && !imageSizes.includes(wantSize)) return -1;

  let score = 0;
  if (wantWood && imageWood === wantWood) score += 20;
  if (wantSize && imageSizes.includes(wantSize)) score += 20;

  for (const part of variantOptionParts(variant)) {
    if (isWoodMaterialToken(part)) continue;
    if (/^\d{2,3}x\d{2,3}$/.test(normalizeSize(part))) continue;
    for (const needle of imageMatchNeedles(part)) {
      if (haystack.includes(needle)) {
        score += needle.length >= 5 ? 3 : 2;
        break;
      }
    }
  }
  return score;
};

/** Best image URL for a variant — featured_image first, then filename token match on product.images. */
export const resolveVariantImageSrc = (
  product: ShopifyJsProduct,
  variant: ShopifyJsVariant | null | undefined,
): string => {
  if (variant?.featured_image?.src) return variant.featured_image.src;

  const images = product.images?.length ? product.images : product.featured_image ? [product.featured_image] : [];

  if (variant && images.length) {
    let best = "";
    let bestScore = -1;
    for (const src of images) {
      const score = scoreImageForVariant(src, variant);
      if (score < 0) continue;
      if (score > bestScore) {
        bestScore = score;
        best = src;
      }
    }
    if (best) return best;
  }

  return product.featured_image || images[0] || "";
};

// ponytail: module self-check — fails fast if filename heuristics regress
(() => {
  const kieferWeiss = { option1: "Kiefer", option2: "Weiß", option3: null } as ShopifyJsVariant;
  const kieferProduct = {
    featured_image: "//cdn.example/ehrenkind-ablage-plano-eich.jpg",
    images: ["//cdn.example/seitenablage-plano-kiefer-natur.jpg", "//cdn.example/seitenablage-plano-kiefer-weiss.jpg"],
  } as ShopifyJsProduct;
  const kieferSrc = resolveVariantImageSrc(kieferProduct, kieferWeiss);
  if (!normalizeToken(kieferSrc).includes("weiss")) {
    throw new Error("[ek-berater] variantImage self-check failed (kiefer/weiss)");
  }

  const birkeKlarlack = { option1: "Birke", option2: "Klarlack", option3: null } as ShopifyJsVariant;
  const birkeProduct = {
    featured_image: "//cdn.example/ehrenkind-ablage-plano-eich.jpg",
    images: [
      "//cdn.example/seitenablage-plano-kiefer-natur.jpg",
      "//cdn.example/ehrenkind-seitenablage-familienbett-plano-birke.jpg",
    ],
  } as ShopifyJsProduct;
  const birkeSrc = resolveVariantImageSrc(birkeProduct, birkeKlarlack);
  if (!normalizeToken(birkeSrc).includes("birke")) {
    throw new Error("[ek-berater] variantImage self-check failed (birke/klarlack)");
  }

  const buche90 = { option1: "90 × 200", option2: null, option3: null } as ShopifyJsVariant;
  const bucheBedProduct = {
    featured_image: "//cdn.example/ehrenkind-plano-jugendbett-buche-140x200.jpg",
    images: [
      "//cdn.example/ehrenkind-plano-jugendbett-buche-140x200.jpg",
      "//cdn.example/ehrenkind-einzelbett-90x200-plano-buche.jpg",
      "//cdn.example/einzelbett-plano-buche-fsc.jpg",
    ],
  } as ShopifyJsProduct;
  const buche90Src = resolveVariantImageSrc(bucheBedProduct, buche90);
  if (!normalizeToken(buche90Src).includes("90x200")) {
    throw new Error("[ek-berater] variantImage self-check failed (buche/90x200)");
  }

  const babyNaturWeiss = { option1: "Natur-Weiß", option2: "60 × 120", option3: "Babybett Pur" } as ShopifyJsVariant;
  const babyWeiss = { option1: "Weiß", option2: "60 × 120", option3: "Babybett Pur" } as ShopifyJsVariant;
  const babyProduct = {
    featured_image: "//cdn.example/babybett-pur-weiss.jpg",
    images: [
      "//cdn.example/babybett-pur-natur-weiss.jpg",
      "//cdn.example/babybett-pur-weiss.jpg",
      "//cdn.example/babybett-pur-klarlack.jpg",
    ],
  } as ShopifyJsProduct;
  const naturWeissSrc = resolveVariantImageSrc(babyProduct, babyNaturWeiss);
  const weissSrc = resolveVariantImageSrc(babyProduct, babyWeiss);
  if (!normalizeToken(naturWeissSrc).includes("naturweiss")) {
    throw new Error("[ek-berater] variantImage self-check failed (natur-weiss image)");
  }
  if (normalizeToken(weissSrc).includes("naturweiss")) {
    throw new Error("[ek-berater] variantImage self-check failed (plain weiss image)");
  }
})();
